<template>
  <div class="tag-picker">
    <!-- 工作区容器：与控制栏 chip 条同一套视觉，滚动由父级分配高度 -->
    <div class="tag-picker__workspace">
      <div class="tag-picker__toolbar">
        <el-input
          v-model="searchTag"
          placeholder="搜索 Tag"
          size="mini"
          clearable
          class="tag-picker__search"
          prefix-icon="el-icon-search"
          @input="debounceSearch"
        />
      </div>

      <!-- 已选 Tag dock：置于上方，点击向下展开 -->
      <button
        type="button"
        class="tag-picker__selectedDock"
        :class="{
          'tag-picker__selectedDock--active': selectedOverlayOpen,
          'tag-picker__selectedDock--empty': !selectedTags.length,
        }"
        :disabled="!selectedTags.length"
        @click="toggleSelectedOverlay"
      >
        <i class="el-icon-price-tag" aria-hidden="true" />
        <span class="tag-picker__selectedDockText">
          <template v-if="selectedTags.length">
            已选 Tag · {{ selectedTags.length }} 个
          </template>
          <template v-else>点击下方卡片选择 Tag</template>
        </span>
        <span v-if="selectedTags.length" class="tag-picker__selectedDockAction">
          {{ selectedOverlayOpen ? '收起' : '查看' }}
        </span>
      </button>

      <div class="tag-picker__scrollWrap">
      <div class="tag-picker__scroll" @wheel="handleScrollWheel">
        <!-- 一级：分类卡片墙，点击进入二级 -->
        <div v-if="!activeGroup" class="tag-picker__l1">
          <div v-if="filteredGroups.length" class="tag-picker__cardGrid">
            <button
              v-for="group in filteredGroups"
              :key="group.key"
              type="button"
              class="tag-picker__l1Card"
              @click="enterGroup(group)"
            >
              <span class="tag-picker__l1CardTop">
                <span class="tag-picker__l1CardKind">
                  {{ childCount(group) ? '分类' : '标签' }}
                </span>
                <span
                  v-if="selectedCountInGroup(group)"
                  class="tag-picker__l1CardBadge"
                >
                  已选 {{ selectedCountInGroup(group) }}
                </span>
              </span>
              <span class="tag-picker__l1CardName">{{ group.name }}</span>
              <span v-if="childCount(group)" class="tag-picker__l1CardCount">
                {{ childCount(group) }} 个子 Tag
              </span>
            </button>
          </div>
        <el-empty v-else description="未找到匹配的 Tag" :image-size="48" class="tag-picker__empty" />
        </div>

        <!-- 二级：当前一级下的 Tag 多选（chip 条，与控制栏视觉一致） -->
        <div v-else class="tag-picker__l2">
          <div class="tag-picker__l2Header">
            <button type="button" class="tag-picker__backBtn" @click="leaveGroup">
              <i class="el-icon-arrow-left" aria-hidden="true" />
              返回全部分类
            </button>
            <span class="tag-picker__l2Title">{{ activeGroup.name }}</span>
          </div>

          <div class="tag-picker__l2Section">
            <div class="tag-picker__l2SectionLabel">本级 Tag</div>
            <div class="tag-picker__chipStrip">
              <button
                type="button"
                class="tag-picker__chip"
                :class="{ 'tag-picker__chip--active': isSelected(activeGroup.key) }"
                @click="toggleTag(activeGroup)"
              >
                {{ activeGroup.name }}
              </button>
            </div>
          </div>

          <div v-if="filteredChildren.length" class="tag-picker__l2Section">
            <div class="tag-picker__l2SectionLabel">子 Tag</div>
            <div class="tag-picker__chipStrip tag-picker__chipStrip--multi">
              <button
                v-for="child in filteredChildren"
                :key="child.key"
                type="button"
                class="tag-picker__chip"
                :class="{ 'tag-picker__chip--active': isSelected(child.key) }"
                @click="toggleTag(child)"
              >
                {{ child.name }}
              </button>
            </div>
          </div>
          <div v-else-if="childCount(activeGroup)" class="tag-picker__l2Empty">
            当前搜索下无匹配的子 Tag
          </div>
        </div>

        <el-empty
          v-if="!allTagList.length && !searchTag"
          description="暂无 Tag"
          :image-size="48"
          class="tag-picker__empty"
        />
      </div>

      <transition name="tag-picker-selected-sheet">
        <div
          v-if="selectedOverlayOpen && selectedTags.length"
          class="tag-picker__selectedLayer"
          role="dialog"
          aria-modal="true"
          aria-label="已选 Tag"
        >
          <div class="tag-picker__selectedPanel">
            <div class="tag-picker__selectedPanelHead">
              <span class="tag-picker__selectedPanelTitle">
                已选 Tag · {{ selectedTags.length }} 个
              </span>
              <button
                type="button"
                class="tag-picker__selectedCloseBtn"
                @click="closeSelectedOverlay"
              >
                收起
              </button>
            </div>
            <div class="tag-picker__selectedChips">
              <el-tag
                v-for="tag in selectedTags"
                :key="tag.key"
                closable
                size="mini"
                class="tag-picker__selectedChip"
                @close="removeTag(tag.key)"
              >
                {{ tag.name }}
              </el-tag>
            </div>
          </div>
          <div
            class="tag-picker__selectedBackdrop"
            aria-hidden="true"
            @click="closeSelectedOverlay"
            @wheel.prevent
          />
        </div>
      </transition>
      </div>
    </div>
  </div>
</template>

<script>
import { nestedAll } from '../../../../api/tagManage'

/**
 * 批量打标 · 页面 Tag 选择器。
 * 一级分类卡片墙 → 点击进入二级 chip 多选；已选 Tag 跨分类累积，与 nestedAll 树结构兼容。
 * 高度由 PageTagTab 父容器分配，本组件内部 workspace 区滚动。
 */
export default {
  name: 'BatchTagPicker',
  props: {
    value: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      searchTag: '',
      allTagList: [],
      selectedTags: [...this.value],
      /** 当前 drill-down 的一级 group.key；null 表示卡片墙 */
      activeGroupKey: null,
      timer: null,
      /** 已选 Tag bottom sheet 是否展开 */
      selectedOverlayOpen: false,
    }
  },
  computed: {
    activeGroup() {
      if (this.activeGroupKey == null) {
        return null
      }
      return this.allTagList.find((group) => group.key === this.activeGroupKey) || null
    },
    /** 搜索过滤一级：名称命中或任一子 Tag 名称命中则保留 */
    filteredGroups() {
      const keyword = this.searchTag.trim().toLowerCase()
      if (!keyword) {
        return this.allTagList
      }
      return this.allTagList.filter((group) => {
        if (group.name.toLowerCase().includes(keyword)) {
          return true
        }
        return (group.tagVOList || []).some((child) =>
          child.name.toLowerCase().includes(keyword)
        )
      })
    },
    /** 二级列表：有搜索词时只展示名称匹配的子 Tag */
    filteredChildren() {
      if (!this.activeGroup) {
        return []
      }
      const children = this.activeGroup.tagVOList || []
      const keyword = this.searchTag.trim().toLowerCase()
      if (!keyword) {
        return children
      }
      return children.filter((child) => child.name.toLowerCase().includes(keyword))
    },
  },
  watch: {
    value(next) {
      this.selectedTags = [...next]
    },
    /** 搜索后若当前一级不在结果里，自动退回卡片墙避免空 drill-down */
    filteredGroups(groups) {
      if (this.activeGroupKey == null) {
        return
      }
      const stillVisible = groups.some((group) => group.key === this.activeGroupKey)
      if (!stillVisible) {
        this.activeGroupKey = null
      }
    },
  },
  created() {
    this.loadTags()
  },
  beforeDestroy() {
    clearTimeout(this.timer)
  },
  methods: {
    loadTags() {
      nestedAll(this.searchTag).then((res) => {
        if (res.success) {
          this.allTagList = res.data || []
        }
      })
    },
    debounceSearch() {
      clearTimeout(this.timer)
      this.timer = setTimeout(() => {
        this.loadTags()
      }, 200)
    },
    childCount(group) {
      return (group.tagVOList || []).length
    },
    isSelected(tagKey) {
      return this.selectedTags.some((tag) => tag.key === tagKey)
    },
    /** 统计某一级下（含本级 + 子级）已选数量，用于卡片角标 */
    selectedCountInGroup(group) {
      const childKeys = new Set((group.tagVOList || []).map((child) => child.key))
      return this.selectedTags.filter(
        (tag) => tag.key === group.key || childKeys.has(tag.key)
      ).length
    },
    enterGroup(group) {
      // 无子 Tag 时直接在卡片上完成勾选，省去空二级页
      if (!this.childCount(group)) {
        this.toggleTag(group)
        return
      }
      this.activeGroupKey = group.key
    },
    leaveGroup() {
      this.activeGroupKey = null
    },
    toggleTag(tag) {
      const index = this.selectedTags.findIndex((item) => item.key === tag.key)
      if (index >= 0) {
        this.selectedTags.splice(index, 1)
      } else {
        this.selectedTags.push({ key: tag.key, name: tag.name })
      }
      this.emitChange()
    },
    removeTag(tagKey) {
      this.selectedTags = this.selectedTags.filter((tag) => tag.key !== tagKey)
      if (!this.selectedTags.length) {
        this.selectedOverlayOpen = false
      }
      this.emitChange()
    },
    toggleSelectedOverlay() {
      if (!this.selectedTags.length) {
        return
      }
      this.selectedOverlayOpen = !this.selectedOverlayOpen
    },
    closeSelectedOverlay() {
      this.selectedOverlayOpen = false
    },
    emitChange() {
      this.$emit('input', [...this.selectedTags])
      this.$emit('change', [...this.selectedTags])
    },
    /**
     * 阻止 Tag 区滚轮事件链式传递到页面背景，避免弹窗打开时整页闪烁。
     */
    handleScrollWheel(event) {
      const el = event.currentTarget
      if (!el) {
        return
      }
      const delta = event.deltaY
      const atTop = el.scrollTop <= 0
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1
      if ((delta < 0 && atTop) || (delta > 0 && atBottom)) {
        event.preventDefault()
      }
      event.stopPropagation()
    },
  },
}
</script>

<style src="../association/pickerShared.css"></style>
